import axios from 'axios'
import { adminApiInstance } from './adminService'

export interface User {
  user_id: string
  email: string
  phone: string
  first_name: string
  last_name: string
  is_active: boolean
  is_verified: boolean
  roles: string[]
  primary_role: string
  created_at: string
  updated_at: string
  // Extended fields
  auth_method?: string
  is_locked?: boolean
  locked_until?: string | null
  last_login_at?: string | null
  last_login_ip?: string | null
  deactivated_at?: string | null
  deleted_at?: string | null
  anonymized_at?: string | null
  scheduled_deletion_at?: string | null
  terms_accepted_at?: string | null
  privacy_policy_accepted_at?: string | null
  marketing_consent?: boolean
  profile_picture_url?: string | null
}

export interface CreateUserDto {
  email: string
  phone: string
  first_name: string
  last_name: string
  role?: string
  password?: string
  is_verified?: boolean
  marketing_consent?: boolean
}

export interface UpdateUserDto {
  email?: string
  phone?: string
  first_name?: string
  last_name?: string
  role?: string
  password?: string
  is_active?: boolean
  is_verified?: boolean
  marketing_consent?: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
}

export interface UserFilters {
  page?: number
  limit?: number
  role?: string
  is_active?: boolean
  search?: string
  auth_method?: string
  is_locked?: boolean
  is_verified?: boolean
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface UserSession {
  session_id: string
  user_id: string
  ip_address: string
  user_agent: string
  device_info: string
  browser: string
  os: string
  is_current: boolean
  created_at: string
  last_activity_at: string
  expires_at: string
}

export interface OAuthAccount {
  oauth_id: string
  user_id: string
  provider: string
  provider_user_id: string
  email: string
  name: string
  avatar_url: string | null
  is_primary: boolean
  created_at: string
  updated_at: string
}

export interface LoginHistoryEntry {
  login_id: string
  user_id: string
  method: string
  ip_address: string
  user_agent: string
  device_info: string
  status: 'success' | 'failure'
  failure_reason: string | null
  created_at: string
}

export interface BulkOperationRequest {
  userIds: string[]
  operation: 'activate' | 'deactivate' | 'delete' | 'lock' | 'unlock' | 'assign_role'
  params?: Record<string, any>
}

export interface BulkOperationResponse {
  success: boolean
  message: string
  affected_count: number
  results: Array<{
    user_id: string
    success: boolean
    error?: string
  }>
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const userApi = axios.create({
  baseURL: `${API_BASE_URL}/users`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
userApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
userApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const userService = {
  // Get all users with pagination and filtering
  async getAll(filters: UserFilters = {}): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.role) params.append('role', filters.role)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.search) params.append('search', filters.search)
    if (filters.auth_method) params.append('auth_method', filters.auth_method)
    if (filters.is_locked !== undefined) params.append('is_locked', filters.is_locked.toString())
    if (filters.is_verified !== undefined) params.append('is_verified', filters.is_verified.toString())
    if (filters.sort_by) params.append('sort_by', filters.sort_by)
    if (filters.sort_order) params.append('sort_order', filters.sort_order)

    const response = await userApi.get('', { params })
    return {
      data: response.data.data,
      total: response.data.total
    }
  },

  // Get user by ID
  async getById(id: string): Promise<User> {
    const response = await userApi.get(`/${id}`)
    return response.data.data
  },

  // Create new user
  async create(userData: CreateUserDto): Promise<User> {
    const response = await userApi.post('', userData)
    return response.data.data
  },

  // Update user
  async update(id: string, userData: UpdateUserDto): Promise<User> {
    const response = await userApi.put(`/${id}`, userData)
    return response.data.data
  },

  // Delete user (soft delete)
  async delete(id: string): Promise<{ message: string }> {
    const response = await userApi.delete(`/${id}`)
    return response.data
  },

  // Account Lifecycle Methods
  async deactivateUser(id: string): Promise<User> {
    const response = await userApi.post(`/${id}/deactivate`)
    return response.data.data
  },

  async reactivateUser(id: string): Promise<User> {
    const response = await userApi.post(`/${id}/reactivate`)
    return response.data.data
  },

  async anonymizeUser(id: string): Promise<{ message: string }> {
    const response = await userApi.post(`/${id}/anonymize`)
    return response.data
  },

  async lockUser(id: string, hours: number = 24): Promise<User> {
    const response = await userApi.post(`/${id}/lock`, { hours })
    return response.data.data
  },

  async unlockUser(id: string): Promise<User> {
    const response = await userApi.post(`/${id}/unlock`)
    return response.data.data
  },

  // Session Management
  async getUserSessions(id: string): Promise<UserSession[]> {
    const response = await userApi.get(`/${id}/sessions`)
    return response.data.data || response.data
  },

  async terminateSession(userId: string, sessionId: string): Promise<{ message: string }> {
    const response = await userApi.delete(`/${userId}/sessions/${sessionId}`)
    return response.data
  },

  async terminateAllSessions(userId: string): Promise<{ message: string }> {
    const response = await userApi.delete(`/${userId}/sessions`)
    return response.data
  },

  // OAuth Methods
  async getUserOAuthAccounts(id: string): Promise<OAuthAccount[]> {
    const response = await userApi.get(`/${id}/oauth-accounts`)
    return response.data.data || response.data
  },

  // Login History
  async getUserLoginHistory(id: string, limit: number = 50, offset: number = 0): Promise<{ data: LoginHistoryEntry[]; total: number }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    const response = await userApi.get(`/${id}/login-history`, { params })
    return response.data
  },

  // Bulk Operations
  async bulkOperation(request: BulkOperationRequest): Promise<BulkOperationResponse> {
    const response = await userApi.post('/bulk', request)
    return response.data
  },

  // Impersonation
  async impersonateUser(adminId: string, userId: string, reason: string): Promise<{ token: string; expires_at: string }> {
    const response = await userApi.post(`/${userId}/impersonate`, {
      admin_id: adminId,
      reason
    })
    return response.data.data || response.data
  },

  // Export
  async exportUsers(format: 'csv' | 'json', filters?: {
    fields?: string[]
    date_from?: string
    date_to?: string
  }): Promise<Blob> {
    const params = new URLSearchParams()
    params.append('format', format)
    if (filters?.fields) params.append('fields', filters.fields.join(','))
    if (filters?.date_from) params.append('date_from', filters.date_from)
    if (filters?.date_to) params.append('date_to', filters.date_to)
    
    const response = await userApi.get('/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  // Get user statistics
  async getStatistics(): Promise<{
    total: number
    active: number
    inactive: number
    byRole: Record<string, number>
  }> {
    try {
      // Statistics endpoint is in admin-service, not user-service
      const response = await adminApiInstance.get('/users/statistics')
      const data = response.data.data
      const overview = data.overview
      const byRole: Record<string, number> = {}
      data.role_distribution.forEach((r: { role_name: string; count: number }) => {
        byRole[r.role_name] = Number(r.count)
      })
      return {
        total: parseInt(overview.total_users),
        active: parseInt(overview.active_users),
        inactive: parseInt(overview.inactive_users) + parseInt(overview.deactivated_users),
        byRole
      }
    } catch (error) {
      // Fallback: get all users and compute stats locally
      const users = await this.getAll()
      const total = users.total
      const active = users.data.filter(u => u.is_active).length
      const inactive = total - active
      const byRole: Record<string, number> = {}
      users.data.forEach(user => {
        user.roles.forEach(role => {
          byRole[role] = (byRole[role] || 0) + 1
        })
      })
      return { total, active, inactive, byRole }
    }
  }
}

export default userService
